import React, {useEffect, useState} from 'react';
import analytics from '@react-native-firebase/analytics';
import {
  SectionHeader,
  TitleColumn,
  Subtitle,
  SectionContainer,
  Title,
  SectionTitleRow,
  CrownWrapper,
  CrownIcon,
  ArrowIcon,
  StoreListContainer,
  SectionTouchable,
} from './styles';
import {ClubeAcessoModal} from '../../../../components/Interface/ClubeAcessoModal';

import {useIsFocused, useNavigation} from '@react-navigation/native';
import {StoreListHome} from './components/StoreListHome';
import {HorizontalStoreShimmer} from '../../../../components/Interface/Loadings/ClubeShimmer/HorizontalStoreShimmer';

import {ParceiroDTO} from '../../../../dtos/ParceiroDTO';
import {api, apiMasterClin} from '../../../../services/api';
import {getCache, setCache, CACHE_KEYS} from '../../../../services/cache';

import {useAuth} from '../../../../context/auth';

type CodigoSolicitado = 'codigo' | 'placa';

type ClubeSectionProps = {
  externalOpen?: boolean;
  onExternalOpenHandled?: () => void;
};

type StepTwoConfig = {
  label: string;
  placeholder: string;
  subtitle: string;
};

const defaultStepTwoConfig: StepTwoConfig = {
  label: 'Código pessoal',
  placeholder: 'Informe seu código',
  subtitle:
    'Cupom válido! Agora informe seu código pessoal dentro do clube para liberar o acesso às vantagens.',
};

const PLACA_MASK = '[AAA]-[0AAA0]';
const PLACA_AFFINE_MASKS = ['[AAA]-[0000]'];

export function ClubeSection({
  externalOpen,
  onExternalOpenHandled,
}: ClubeSectionProps) {
  const navigation = useNavigation();

  const {isLogged, openSign} = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [showClubeModal, setShowClubeModal] = useState(false);
  const [codigoSolicitado, setCodigoSolicitado] =
    useState<CodigoSolicitado>('codigo');
  const [stepTwoConfig, setStepTwoConfig] = useState<StepTwoConfig>({
    ...defaultStepTwoConfig,
  });

  const isFocused = useIsFocused();

  const [parceirosDestaque, setParceirosDestaque] = useState<ParceiroDTO[]>(
    [] as ParceiroDTO[],
  );

  const shouldMaskPlaca = codigoSolicitado === 'placa';

  function handleCloseClubeModal() {
    resetClubeAccessState();
    setShowClubeModal(false);
    onExternalOpenHandled?.();
  }

  function handleCodigoChange(formatted: string, extracted?: string) {
    if (!shouldMaskPlaca) {
      return undefined;
    }

    const sanitized = (extracted || formatted || '')
      .replace(/[^A-Za-z0-9]/g, '')
      .toUpperCase();

    return sanitized;
  }

  useEffect(() => {
    let isMounted = true;

    async function getParceiros() {
      // Tentar buscar do cache primeiro
      const cached = await getCache<ParceiroDTO[]>(
        CACHE_KEYS.MASTERCLIN_DESTAQUES,
      );
      if (cached && isMounted) {
        setParceirosDestaque(cached);
        setIsLoading(false);
        return;
      }

      await apiMasterClin
        .get('/parceiro/obter-destaques')
        .then(({data}) => {
          const {parceiros} = data;
          if (isMounted) {
            setParceirosDestaque(parceiros);
            // Salvar no cache por 7 dias
            setCache(CACHE_KEYS.MASTERCLIN_DESTAQUES, parceiros);
          }
        })
        .then(() => {
          setIsLoading(false);
        });
    }

    getParceiros();

    return () => {
      isMounted = false;
    };
  }, [isFocused]);

  useEffect(() => {
    if (externalOpen) {
      handleClubeBeneficios();
      onExternalOpenHandled?.();
    }
  }, [externalOpen, onExternalOpenHandled]);

  function resetClubeAccessState() {
    setCodigoSolicitado('codigo');
    setStepTwoConfig({...defaultStepTwoConfig});
  }

  function handleClubeBeneficios() {
    if (!isLogged) {
      return openSign();
    }

    resetClubeAccessState();
    analytics().logEvent('home_clica_banner_clube_beneficios');
    setShowClubeModal(true);
  }

  async function validateCupom(cupom: string) {
    const normalizedCupom = cupom.trim().toLowerCase().replace(/\s+/g, '');

    if (normalizedCupom.length < 3) {
      return {
        ok: false,
        message: 'Digite um cupom válido para continuar.',
      };
    }

    try {
      const {data} = await api.get('/solicitaPlaca', {
        params: {
          codigoDesconto: normalizedCupom,
          masterclin: true,
        },
      });

      if (!data) {
        await api.get('/clube_masterclin', {
          params: {
            codigoDesconto: normalizedCupom,
          },
        });

        handleClubeSuccess(normalizedCupom, '');
        return {ok: true, complete: true, codigo: ''};
      }

      const solicitacao: CodigoSolicitado =
        data === 'placa' ? 'placa' : 'codigo';

      setCodigoSolicitado(solicitacao);

      if (solicitacao === 'placa') {
        setStepTwoConfig({
          label: 'Placa do veículo',
          placeholder: 'Digite a placa solicitada',
          subtitle:
            'Cupom válido! Agora informe a placa solicitada para liberar o acesso ao clube.',
        });
      } else {
        setStepTwoConfig({...defaultStepTwoConfig});
      }

      return {ok: true};
    } catch (error: any) {
      const message =
        error?.response?.data?.msg ||
        error?.response?.data?.error ||
        'Não conseguimos validar o cupom. Tente novamente.';

      return {ok: false, message};
    }
  }

  async function validateCodigoPessoal(codigoPessoal: string, cupom?: string) {
    const codigoLimpo =
      codigoSolicitado === 'placa'
        ? codigoPessoal.trim().replace(/\s+/g, '').toUpperCase()
        : codigoPessoal.trim();

    const cupomNormalizado = cupom
      ? cupom.trim().toLowerCase().replace(/\s+/g, '')
      : '';

    if (codigoLimpo.length < 3) {
      return {
        ok: false,
        message:
          codigoSolicitado === 'placa'
            ? 'Revise a placa informada.'
            : 'Revise seu código pessoal dentro do clube.',
      };
    }

    if (!cupomNormalizado) {
      return {
        ok: false,
        message: 'Digite um cupom válido para continuar.',
      };
    }

    try {
      await api.get('/clube_masterclin', {
        params:
          codigoSolicitado === 'placa'
            ? {
                codigoDesconto: cupomNormalizado,
                placa: codigoLimpo,
              }
            : {
                codigoDesconto: cupomNormalizado,
                codigo: codigoLimpo,
              },
      });

      return {ok: true};
    } catch (error: any) {
      const message =
        error?.response?.data?.msg ||
        error?.response?.data?.error ||
        (codigoSolicitado === 'placa'
          ? 'Não foi possível validar a placa informada.'
          : 'Não foi possível validar o código informado.');

      return {ok: false, message};
    }
  }

  function handleClubeSuccess(cupom: string, codigoPessoal: string) {
    analytics().logEvent('clube_validacao_sucesso', {
      cupom,
      codigo_pessoal: codigoPessoal,
    });

    resetClubeAccessState();
    setShowClubeModal(false);
    navigation.navigate('LoadingClube' /**{cupom, codigoPessoal}**/);
  }

  return (
    <SectionTouchable  onPress={handleClubeBeneficios} activeOpacity={0.85}>
      <SectionContainer>
        <SectionHeader>
          <SectionTitleRow>
            <CrownWrapper>
              <CrownIcon />
            </CrownWrapper>
            <TitleColumn>
              <Title>+ 10 mil lojas para você</Title>
              <Subtitle>Aproveite benefícios exclusivos</Subtitle>
            </TitleColumn>
          </SectionTitleRow>
          <ArrowIcon onPress={() => navigation.navigate('HowTo')} />
        </SectionHeader>

      </SectionContainer>

      <StoreListContainer>
        {!isLoading && parceirosDestaque.length > 0 ? (
          <StoreListHome
            parceiros={parceirosDestaque}

          />
        ) : (
          <HorizontalStoreShimmer showDivider={false} />
        )}
      </StoreListContainer>

      <ClubeAcessoModal
        isVisible={showClubeModal}
        onDismiss={handleCloseClubeModal}
        onSuccess={handleClubeSuccess}
        validateCupom={validateCupom}
        validateCodigoPessoal={validateCodigoPessoal}
        codigoLabel={stepTwoConfig.label}
        codigoPlaceholder={stepTwoConfig.placeholder}
        stepTwoSubtitle={stepTwoConfig.subtitle}
        codigoMask={shouldMaskPlaca ? PLACA_MASK : undefined}
        codigoMaskAffineFormats={
          shouldMaskPlaca ? PLACA_AFFINE_MASKS : undefined
        }
        onCodigoChange={shouldMaskPlaca ? handleCodigoChange : undefined}
      />
    </SectionTouchable>
  );
}
